import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { isReportCompleted } from '@repo/shared/schema';
import { Button } from '@repo/ui/button';
import { BookmarkIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { ReviewChart } from '@/components/raven/charts';
import { Notes } from '@/components/raven/notes';
import { Profile } from '@/components/raven/profile';
import { ReportHistory, ReportRows } from '@/components/raven/report-history';
import { dateLabel, OLink, QueryState, RiskBadge } from '@/components/raven/shared';
import { useONavigate } from '@/hooks/use-organization-navigate';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';
import { DocumentsSection } from '../reports/[reportId]/components/documents-section';
import { KeyMembersAccordion } from '../reports/[reportId]/components/key-members-accordion';
import { SignalsSection } from '../reports/[reportId]/components/signals-section';

export const ClientProfilePage = () => {
  useLocale();

  const { clientId = '' } = useParams();
  const navigate = useONavigate();
  const role = useOrganizationStore((state) => state.currentOrganization.currentOrganizationRole);
  const [selectedId, setSelectedId] = useState('');
  const [days, setDays] = useState(0);
  const query = trpc.workspace.getClient.useQuery({ clientId }, { enabled: Boolean(clientId) });
  const client = query.data;
  const profile =
    client?.reports.find((report) => report._id === client.latestReportId) ?? client?.reports[0];
  const reports =
    client?.reports.filter(
      (report) => !days || new Date(report.createdAt).getTime() >= Date.now() - days * 86400000,
    ) ?? [];
  const selected =
    selectedId && reports.some((report) => report._id === selectedId)
      ? selectedId
      : (reports.find((report) => isReportCompleted(report.status))?._id ?? '');
  const detail = trpc.report.getReport.useQuery(
    { reportId: selected },
    { enabled: Boolean(selected) },
  );
  const utils = trpc.useUtils();
  const onError = (cause: unknown) => toast.error(errorMessage(AppError.parse(cause)));
  const bookmark = trpc.workspace.updateBookmark.useMutation({
    onSuccess: () => {
      void utils.workspace.invalidate();
    },
    onError,
  });
  const create = trpc.report.createReport.useMutation({
    onSuccess: (report) => navigate({ to: `/reports/create/${report._id}` }),
    onError,
  });
  if (!client || !profile) {
    return (
      <main className="raven-page">
        <QueryState
          error={query.isError}
          loading={query.isLoading}
          retry={() => void query.refetch()}
        />
      </main>
    );
  }
  const generated = client.reports.filter((report) => isReportCompleted(report.status));
  const documents = [
    ...new Map(
      [...client.reports]
        .reverse()
        .flatMap((report) => report.documents ?? [])
        .map((document) => [document.fileId, document]),
    ).values(),
  ];
  const newReport = (
    <Button
      disabled={create.isPending || role === 'ORG_VIEWER'}
      onClick={() => create.mutate({ reportType: client.reportType, clientId })}
    >
      <PlusIcon className="size-4" />
      {t('auth.generateNewReport')}{' '}
    </Button>
  );
  return (
    <main className="raven-page">
      <div className="mb-8 flex justify-between gap-4">
        <OLink to="/clients">{t('auth.backToClients')}</OLink>
        <nav className="flex gap-7">
          {client.previousId && (
            <OLink to={`/clients/${client.previousId}`}>{t('auth.previousClient')}</OLink>
          )}
          {client.nextId && <OLink to={`/clients/${client.nextId}`}>{t('auth.nextClient')}</OLink>}
        </nav>
      </div>
      <span className="raven-label">{t('workspace.clientProfile')}</span>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1>{t('auth.seeTheClientDetails')}</h1>
          <p className="max-w-lg text-sm">
            {t('auth.comprehensiveInformationAboutTheClientReviewProgressAndSubmitted')}{' '}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="secondary">
            <OLink to={`/reports/${generated[0]?._id ?? profile._id}`}>
              {t('auth.viewLatestReport')}
            </OLink>
          </Button>
          {newReport}
        </div>
      </div>
      <nav aria-label={t('auth.clientSections')} className="raven-tabs">
        <a href="#profile">{t('auth.profile')}</a>
        {client.reportType === 'KYB' && <a href="#key-people">{t('auth.keyPeople')}</a>}
        <a href="#history">{t('auth.reviewHistory')}</a>
        <a href="#documents">{t('auth.documents')}</a>
        <a href="#reports">{t('auth.generatedReports')}</a>
      </nav>
      <section className="grid gap-5 lg:grid-cols-[0.85fr_1.2fr]" id="profile">
        <div className="raven-panel">
          <div className="mb-4 flex items-center justify-between">
            <span className="raven-muted">
              {t('auth.clientId')} {client._id}
            </span>
            <Button
              aria-label={client.bookmarked ? t('auth.removeBookmark') : t('auth.bookmarkClient')}
              disabled={bookmark.isPending}
              onClick={() =>
                bookmark.mutate({ clientId, data: { bookmarked: !client.bookmarked } })
              }
              size="icon"
              variant="ghost"
            >
              <BookmarkIcon className={`size-4 ${client.bookmarked ? 'fill-current' : ''}`} />
            </Button>
          </div>
          <Profile
            embedded
            metadata={
              <dl className="mt-5 space-y-3 border-b pb-5 text-xs">
                <div className="flex justify-between">
                  <dt>{t('auth.firstSubmissionDate')}</dt>
                  <dd>{dateLabel(client.reports.at(-1)?.createdAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t('auth.lastReviewDate')}</dt>
                  <dd>{dateLabel(profile.decision?.decidedAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>{t('reports.clientType')}</dt>
                  <dd>
                    {client.reportType === 'KYB' ? t('reports.business') : t('reports.individual')}
                  </dd>
                </div>
              </dl>
            }
            report={profile}
          />
        </div>
        <div className="raven-panel">
          <h2>{t('auth.ravenReviewSummary')}</h2>
          <div className="my-5 flex items-center justify-between text-xs">
            <span>
              {client.name} {t('auth.riskLevelIs')}
            </span>
            <RiskBadge level={client.riskLevel} />
          </div>
          <p className="whitespace-pre-line text-sm">
            {profile.assessment?.profileSummary ??
              profile.riskSummary ??
              t('extra.noReviewSummaryRecorded')}
          </p>
          {profile.assessment?.profileSummary && (
            <p className="mt-4 text-muted-foreground text-sm">{profile.riskSummary}</p>
          )}
          <h3 className="mt-6 mb-3">
            {generated.length
              ? `Last ${Math.min(3, generated.length)} Generated ${generated.length === 1 ? t('workspace.report') : t('workspace.reports')}`
              : t('auth.generatedReports2')}
          </h3>
          <ReportRows reports={generated.slice(0, 3)} />
        </div>
      </section>
      {client.reportType === 'KYB' && (
        <KeyMembersAccordion
          assessment={detail.data?.assessment ?? profile.assessment}
          compact
          keyMembers={detail.data?.keyMembers ?? profile.keyMembers ?? []}
          reportId={detail.data?._id ?? profile._id}
        />
      )}
      <section className="raven-panel raven-section" id="history">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h2>{t('auth.reviewHistory2')}</h2>
            <p className="mt-2 text-sm">
              {t('auth.timelineOfGeneratedReportsAndDetectedRiskLevels')}
            </p>
          </div>
          {newReport}
        </div>
        <div className="mt-7 flex items-center justify-between gap-3">
          <h3>{t('auth.viewReportRiskResultsOverTime')}</h3>
          <select
            aria-label={t('auth.reviewHistoryPeriod')}
            className="raven-select"
            onChange={(event) => setDays(Number(event.target.value))}
            value={days}
          >
            <option value={0}>{t('reports.allTime')}</option>
            <option value={30}>{t('reports.last30Days')}</option>
            <option value={180}>{t('reports.last6Months')}</option>
            <option value={365}>{t('reports.lastYear')}</option>
          </select>
        </div>
        <ReviewChart onSelect={setSelectedId} reports={reports} selectedId={selected} />
        <div className="mb-4 flex items-center justify-between">
          <h3>{t('auth.riskSignalsBasedOnSelectedReport')}</h3>
          {selected && <OLink to={`/reports/${selected}`}>{t('auth.viewReport2')}</OLink>}
        </div>
        <QueryState
          error={detail.isError}
          loading={detail.isLoading && Boolean(selected)}
          retry={() => void detail.refetch()}
        />
        {detail.data && <SignalsSection reportId={selected} signals={detail.data.signals ?? []} />}
      </section>
      <section className="raven-section" id="documents">
        <DocumentsSection documents={documents} />
      </section>
      <section className="raven-panel raven-section" id="reports">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2>{t('auth.generatedReports2')}</h2>
          {newReport}
        </div>
        <p className="mt-2 text-sm">{t('auth.allReportsGeneratedForThisClientSReview')}</p>
        <ReportHistory clientId={clientId} />
      </section>
      <Notes targetId={clientId} targetType="client" />
    </main>
  );
};
